import { RiCalendar2Line, RiDeleteBinLine, RiLockLine } from "@remixicon/react";
import React, {
    useMemo,
    useState,
} from "react";
import {
    GetUserAgentsDocument,
    Permission,
    Token,
    useCreateTokenMutation,
    useDeleteTokenMutation,
    useGetUserAgentsQuery,
} from "../../../../__generated__/api";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import { Filters as FiltersSelector } from "../../../../components/filters/Filters";
import { useDrawerForm } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import TableContainer from "../../../../components/theme/table";
import Time from "../../../../components/time/Time";
import { parseFilterDates, tryParseDate } from "../../../../date";

import CodeBlock from "../../../../components/code-block/CodeBlock";
import Confirm from "../../../../components/confirm/Confirm";
import DateRangeCalendarPicker from "../../../../components/pickers/date-range/DateRangeCalendarPicker";
import PermissionsList, { PERMISSION_MAP, PERMISSION_OPTIONS } from "../../../../components/pickers/permissions/Permissions";
import Badge from "../../../../components/theme/badge/Badge";
import Styles from "./Users.module.scss";

type TokenWithPermissions = Token & {
    permissionList: string;
    date: Date | undefined;
}

const TokenContainer = TableContainer<TokenWithPermissions>();

export function useCreateTokenView() {
    const [createToken] = useCreateTokenMutation();
    const [createdToken, setCreatedToken] = useState<string | undefined>(undefined);

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Create Token",
        size: "medium",
        onHide: () => {
            formTracker.reset();
        },
        onSave: async () => {
            const tokenResponse = await createToken({
                refetchQueries: [
                    { query: GetUserAgentsDocument },
                ],
                variables: {
                    token: {
                        permissions: formTracker.properties.permissions.value ?? [],
                        name: formTracker.properties.name.value,
                    },
                },
            });

            setCreatedToken(tokenResponse.data?.createToken.token as string);
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .property("name", () => "")
                .property<"permissions", Permission[]>("permissions", () => []),
            dependencies: [],
        },
        body: tracker => (
            <>
                <FormGroup
                    label="Name"
                    required
                    value={tracker.properties.name.value}
                    size="small"
                    onChange={event => tracker.properties.name.setValue(event.target.value)}
                />

                <h6 className={Styles["token-title"]}>Permissions</h6>
                <PermissionsList
                    value={tracker.properties.permissions.value}
                    onChange={tracker.properties.permissions.setValue}
                    isMulti
                />
            </>
        ),
    });

    const elements = (
        <>
            {element}
            <Confirm
                shouldShow={createdToken !== undefined}
                onHide={() => setCreatedToken(undefined)}
                size="lg"
                shouldCenter
            >
                <div>
                    <p>
                        This token will only display here now. Once you close this screen, you will be unable to be
                        retrieved again without creating a new token. Please store it in a safe space.
                    </p>
                    <CodeBlock code={createdToken} isCopyable size="sm" />

                </div>
            </Confirm>
        </>
    );

    return {
        element: elements,
        setOpen,
    };
}

function isDateInRange(date: Date | undefined, range: {startDate?: Date; endDate?: Date}) {
    return date && (!range.startDate || date.getTime() >= range.startDate?.getTime())
        && (!range.endDate || date.getTime() <= range.endDate?.getTime());
}

export default function TokensTable() {
    const {
        data, error, loading: isLoading,
    } = useGetUserAgentsQuery();

    const [deleteToken] = useDeleteTokenMutation();

    const tokenData = useMemo(() => data?.tokens?.map(token => ({
        ...token,
        date: tryParseDate(token.createdDate),
        permissionList: token.permissions?.map(permission => PERMISSION_MAP[permission]?.name).filterNull().join(", "),
    }) as TokenWithPermissions), [data]);

    const { element: deleteElement, setDeleting } = useDeleteConfirm<TokenWithPermissions>(
        async token => {
            await deleteToken({
                refetchQueries: [
                    { query: GetUserAgentsDocument },
                ],
                variables: {
                    id: token.id,
                },
            });
        },
        user => user.name,
        "Revoke Token",
        "Are you sure you want to delete and invalidate the token",
        "Revoke Token",
    );

    const { element: createElement, setOpen } = useCreateTokenView();

    return (
        <SettingsContainer.Section
            sectionKey="tokens"
            title="Tokens"
            shouldDisplayTitle={false}
        >
            <SettingsContainer.Setting
                title="Tokens"
                description="Manage Tokens"
                permissions={SettingsContainer.Setting.Permissions.Administrator}
                isFullWidth
            >

                {deleteElement}
                {createElement}
                <TokenContainer data={tokenData as TokenWithPermissions[]} isLoading={isLoading} error={error}>
                    <TokenContainer.Empty>
                        <TokenContainer.Empty.Default
                            text={(
                                <>
                                    To create a new authentication token, click Create a Token below. To find out
                                    more about our developer docs, please see our
                                    {" "}
                                    <a
                                        target="_blank"
                                        href="https://developer.support.foqal.io/"
                                        rel="noreferrer"
                                    >
                                        Developer Portal
                                    </a>
                                </>
                            )}
                            title="Create a Token"
                            icon={<RiLockLine />}
                            onClick={() => setOpen(true)}
                            buttonText="Create a Token"
                        />
                    </TokenContainer.Empty>
                    <TokenContainer.TableControls>
                        <TokenContainer.TableControls.Search
                            search={(query, user) => user.name?.toLocaleLowerCase().includes(query)
                            || user.permissionList.toLocaleLowerCase().includes(query)}
                        />
                        <TokenContainer.TableControls.Seperator />
                        <TokenContainer.TableControls.Filters />

                        <TokenContainer.TableControls.Filter
                            defaultValue={{
                                startDate: undefined as Date | undefined,
                                endDate: undefined as Date | undefined,
                            }}
                            deserialize={keys => (typeof keys === "object" && !Array.isArray(keys) ? ({
                                startDate: keys.startDate ? new Date(keys.startDate) : undefined,
                                endDate: keys.endDate ? new Date(keys.endDate) : undefined,
                            }) : undefined)}
                            serialize={value => ({
                                startDate: value.startDate?.toISOString(),
                                endDate: value.endDate?.toISOString(),
                            })}
                            filterKey="createdDate"
                            onFilter={(item, currentValue) => isDateInRange(item.date, currentValue)}
                        >
                            {(currentValue, setValue) => (
                                <DateRangeCalendarPicker
                                    toggleText="Created"
                                    icon={<RiCalendar2Line />}
                                    value={{
                                        startDate: currentValue.startDate,
                                        endDate: currentValue.endDate,
                                    }}
                                    onChange={change => {
                                        const dates = parseFilterDates(change);
                                        setValue({
                                            startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                            endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                                        });
                                    }}
                                />
                            )}

                        </TokenContainer.TableControls.Filter>
                        <TokenContainer.TableControls.Filter
                            defaultValue={new Set<string>()}
                            deserialize={TokenContainer.TableControls.Filter.Serializers.deserializeSet}
                            serialize={TokenContainer.TableControls.Filter.Serializers.serializeSet}
                            filterKey="permissions"
                            onFilter={(item, selected) => item.permissions?.some(perm => selected.has(perm))}
                        >
                            {(currentValue, setValue) => (
                                <FiltersSelector
                                    toggleText="Permissions"
                                    className=""
                                    icon={<RiLockLine />}
                                    categories={PERMISSION_OPTIONS}
                                    searchMinimum={5}
                                    selectedCategories={currentValue}
                                    onSelected={setValue}
                                />
                            )}

                        </TokenContainer.TableControls.Filter>
                        <TokenContainer.TableControls.SortBy />
                        <TokenContainer.TableControls.Create text="Add Token" onClick={() => setOpen(true)} />
                    </TokenContainer.TableControls>
                    <TokenContainer.Table>
                        <TokenContainer.TableValue
                            columnKey="name"
                            title="Name"
                            sortBy={item => item.name}
                        >
                            {item => item.name}
                        </TokenContainer.TableValue>
                        <TokenContainer.TableValue
                            columnKey="permissions"
                            title="Permissions"
                            sortBy={item => item.permissions?.length}
                            className={SettingsContainer.Setting.BadgeListClassName}
                        >
                            {item => item.permissions?.map(permission => (
                                <Badge key={permission} variant="gray">
                                    {PERMISSION_MAP[permission]?.name}
                                </Badge>
                            ))}
                        </TokenContainer.TableValue>
                        <TokenContainer.TableValue
                            columnKey="createdDate"
                            title="Created Date"
                            sortBy={item => item.date?.getTime()}
                        >
                            {item => (
                                <>
                                    <Time date={item.date} variant="date" />
                                    {" "}
                                    <Time date={item.date} variant="time" />
                                </>
                            )}
                        </TokenContainer.TableValue>
                        <TokenContainer.Action
                            icon={<RiDeleteBinLine />}
                            altText="Revoke Token"
                            onClick={setDeleting}
                        />

                    </TokenContainer.Table>
                </TokenContainer>
            </SettingsContainer.Setting>
        </SettingsContainer.Section>
    );
}
